import {useGetConsentLookupsQuery} from '../../../../api/services/lookupsService';
import {Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useState} from 'react';
import IconButton from '@mui/material/IconButton';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import useNotification from '../../../../utility/hooks/useNotification';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import Stack from '@mui/material/Stack';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PropTypes from 'prop-types';
import Uploader from '../../../SchoolManagement/Setup/Uploader';
import {
    downloadConsentFile,
    useDeleteConsentFileMutation,
    useUploadConsentFileMutation
} from '../../../../api/services/filesService';
import objectToFormData from '../../../../utility/objectToFormData';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import {useConfirm} from 'material-ui-confirm';
import CircularProgress from '@mui/material/CircularProgress';
import {
    useGetProfileDescriptionsQuery,
    useUpdateProfileDescriptionMutation
} from '../../../../api/services/schoolManagementService';
import DescriptionSettings from './DescriptionSettings';
import InfoHover from '../../../../shared-components/InfoHover';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

// Maria, 20 October 2026: each introductory text in a white card, in two columns, in the order the sections appear on
// the student profile, with an 'i' saying where the text appears (from the SIMS Lookups video script)
const CARD_SX = {
    backgroundColor: '#fff',
    border: '1px solid #e3e8ef',
    borderRadius: 2,
    px: 2,
    pt: 1.25,
    pb: 0.75
};

// Maria, 20 October 2026: Vaccination and Passport at the bottom, above Parental Consents
const SECTIONS = [
    {
        key: PROFILE_SECTIONS_KEYS.medicalConditions,
        header: 'Medical Conditions',
        // Maria, 20 October 2026: the full width, as it has the longest text
        wide: true,
        hover: 'The options in this list are imported from your school\'s SIMS lookup list. The Medical Conditions introductory text is the paragraph of text that appears at the top of the Medical Conditions section in the student profile. You can customise this text to guide parents on how to use the medical conditions list. Make sure your introductory text directs parents to select Other if their child\'s condition does not appear on the list. There is sample text in the User Guide that you can copy and paste if needed.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.medicalEvents,
        header: 'Medical Events',
        hover: 'The Medical Events introductory text appears at the top of the Medical Events section in the student profile. The default text asks parents to add any medical event that is not related to a diagnosed medical condition, for example an injury caused by an accident.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.dietaryNeeds,
        header: 'Dietary Needs',
        hover: 'The Dietary Needs introductory text appears at the top of the Dietary Needs section in the student profile. This box can be left empty.',
        canBeEmpty: true
    },
    {
        key: PROFILE_SECTIONS_KEYS.sen,
        header: 'SEN',
        hover: 'The SEN introductory text is the paragraph at the top of the imported SIMS lookup list in the student profile. Include guidance on how to use the list if your school uses broad SEN categories rather than specific condition names. If you have set SEN to Edit for applicants and View only for on-roll students in Manage Access, the same text is shown to all parents, so give separate instructions for each group.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.senViewAccess,
        // AD-2827 (Maria, 30 October 2026)
        header: 'SEN (View access)',
        hover: 'The SEN (View access) introductory text appears at the top of the SEN(D) section in the student profile for parents with View access to SEN in Manage Access. They cannot change the SEN information, so the default text asks them to click on the ‘This information needs to be updated’ button if any of it is not correct.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.contacts,
        // Maria, 29 October 2026 (AD-2817): one box for Edit access, one for View access
        header: 'Contacts (Edit access)',
        hover: 'The Contacts (Edit access) introductory text appears at the top of the Contacts section in the student profile for parents and staff with Edit access to Contacts in Manage Access. You can use it to explain your school\'s rules on adding contacts.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.contactsViewAccess,
        header: 'Contacts (View access)',
        hover: 'The Contacts (View access) introductory text appears at the top of the Contacts section in the student profile for parents with View access to Contacts in Manage Access. They cannot add or edit contacts, so the default text asks them to contact the school with any changes.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.vaccination,
        header: 'Vaccination',
        hover: 'The Vaccination introductory text appears above the vaccination record upload field in the student profile. The default text asks parents to upload their child\'s vaccination record, which can be obtained from their GP surgery. Some schools just use this box to say Vaccination Information is not required by our school.'
    },
    {
        key: PROFILE_SECTIONS_KEYS.passport,
        header: 'Passport',
        hover: 'The Passport introductory text appears above the passport field in the Cultural section of the student profile. The passport field is not compulsory. Some schools just use this box to say Passport Information is not required by our school.'
    }
];

const Lookups = () => {
    const {data: consents = [],} = useGetConsentLookupsQuery();
    const {
        data: descriptionData = [],
        isError: isLoadingDescriptionsFailed
    } = useGetProfileDescriptionsQuery();
    const [updateProfileDescription, {isError: isUpdateProfileDescriptionError}] = useUpdateProfileDescriptionMutation();
    const {showErrorNotification} = useNotification();

    useEffect(() => {
        if (isUpdateProfileDescriptionError) {
            showErrorNotification('Failed to update description');
        }

        if (isLoadingDescriptionsFailed) {
            showErrorNotification('Failed to load profile descriptions');
        }
    }, [isUpdateProfileDescriptionError, isLoadingDescriptionsFailed]);

    const handleDescriptionChange = async (key, value) => {
        await updateProfileDescription({key, value});
    };

    const descriptionFor = key => descriptionData.find(x => x.key === key)?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[key];

    return (
        <Box sx={{pt: 1.5, width: '100%'}}>
            {/* Maria, 20 October 2026: a heading for the page, with the words from the SIMS Lookups video script */}
            <Typography sx={{fontWeight: 600, fontSize: 17, mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.5}}>
                Introductory text for your SIMS lookup lists
                <InfoHover
                    testId="hover-sims-lookups"
                    text="The options in these lists are imported from your school's SIMS lookup lists. Each introductory text appears at the top of its section in the student profile, and can be up to 1,000 characters long. You can use the Placeholders menu to add details such as the student's name or the school's name. Click on Save Description to save your text. To return to the original text supplied by Admicity, click on Revert to Default Description."
                />
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'},
                    gap: 1.5
                }}
            >
                {SECTIONS.map(section => (
                    <Box key={section.key} sx={{...CARD_SX, gridColumn: section.wide ? {lg: '1 / -1'} : 'auto'}} data-testid={`lookup-card-${section.header}`}>
                        <DescriptionSettings
                            header={section.header}
                            hover={section.hover}
                            rows={4}
                            description={descriptionFor(section.key)}
                            defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[section.key]}
                            canBeEmpty={section.canBeEmpty}
                            onDescriptionChange={value => handleDescriptionChange(section.key, value)}
                        />
                    </Box>
                ))}
            </Box>
            <ConsentsSettings
                consents={consents}
                description={descriptionFor(PROFILE_SECTIONS_KEYS.parentalConsents)}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.parentalConsents, value)}
            />
        </Box>
    );
};

const HEAD_CELL_SX = {backgroundColor: '#e3f5f1', fontWeight: 600, py: 0.75};

function ConsentsSettings(
    {
        consents,
        description,
        onDescriptionChange
    }) {
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const [uploadFile, {isLoading: isFileUploading, isError: isUploadError}] = useUploadConsentFileMutation();
    const [deleteFile, {isError: isDeleteError}] = useDeleteConsentFileMutation();

    const downloadFile = useFileDownloader();
    const {showErrorNotification} = useNotification();
    const confirm = useConfirm();

    useEffect(() => {
        if (isUploadError) {
            showErrorNotification('Failed to upload consent document');
        }
        if (isDeleteError) {
            showErrorNotification('Failed to delete consent document');
        }
    }, [isUploadError, isDeleteError]);

    const handleDownloadFile = async (consentCode) =>
        await downloadFile(
            downloadConsentFile,
            consentCode);

    const closeUploadDialog = () => {
        setOpenUploadDialog(false);
        setSelectedFiles([]);
        setIsUploadSizeExceedsLimit(false);
        setSelectedConsent(null);
    };

    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) {
            return;
        }

        await uploadFile(objectToFormData({
            file: selectedFiles[0],
            consentCode: selectedConsent.code
        }));
        closeUploadDialog();
    };

    const handleFileDelete = async consentCode =>
        confirm({
            title: 'Confirm Deletion',
            description:
                'Are you sure you want to delete the consent document? Once deleted, it cannot be recovered.',
            confirmationText: 'Delete',
            cancellationText: 'Cancel',
            confirmationButtonProps: {
                sx: {
                    color: '#186BBE',
                },
            },
            cancellationButtonProps: {
                sx: {
                    colour: '#186BBE',
                },
            },
        })
            .then(async () => await deleteFile({consentCode}))
            .catch(_ => {
            });

    const openUpload = consent => {
        setSelectedConsent(consent);
        setOpenUploadDialog(true);
    };

    // Maria, 20 October 2026: one card for Parental Consents, with its introductory text on the left and the consents
    // with their policy documents in a table on the right
    return (
        <Box sx={{...CARD_SX, mt: 1.5}} data-testid="lookup-card-Parental Consents">
            <Typography sx={{fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5}}>
                Parental Consents
                <InfoHover
                    testId="hover-Parental Consents"
                    text="All consents displayed in the student profile come from your school's Parental Consents lookup list in SIMS. To add, edit, or remove consent options in SIMS, go to Tools, Lookups, Maintain. Changes will appear in Admicity after the next sync. You can add an introductory description to the top of the consents section and upload policy documents for parents to read before confirming each consent. Click on Upload document next to a consent, choose the file, up to 25MB, and click on Upload."
                />
            </Typography>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2, alignItems: 'start'}}>
                <DescriptionSettings
                    fieldLabel={null}
                    rows={6}
                    description={description}
                    defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.parentalConsents]}
                    onDescriptionChange={value => onDescriptionChange(value)}
                />
                <Box sx={{border: '1px solid #e3e8ef', borderRadius: 1, overflow: 'hidden'}}>
                    <Table size="small" data-testid="consent-documents">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL_SX}>Consent</TableCell>
                                <TableCell sx={HEAD_CELL_SX}>Policy document</TableCell>
                                <TableCell sx={{...HEAD_CELL_SX, width: 1, whiteSpace: 'nowrap'}}/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...consents]
                                .sort((a, b) => a.description.localeCompare(b.description))
                                .map((consent, index) => (
                                    <TableRow
                                        key={consent.code ?? index}
                                        sx={{height: 38, '& td': {backgroundColor: index % 2 === 1 ? '#f1faf7' : '#fff', py: 0.25}}}
                                    >
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>{consent.description}</TableCell>
                                        <TableCell sx={{maxWidth: 0, width: '50%'}}>
                                            {consent.file != null && (
                                                <Stack direction="row" alignItems="center" gap={0.75} sx={{minWidth: 0}}>
                                                    <InsertDriveFileRoundedIcon fontSize="small" sx={{color: '#0A8572', flexShrink: 0}}/>
                                                    <Typography variant="body2" noWrap title={consent.file.name}>{consent.file.name}</Typography>
                                                </Stack>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap', textAlign: 'right'}}>
                                            {consent.file != null
                                                ? (
                                                    <>
                                                        <Tooltip title="Download">
                                                            <IconButton size="small" aria-label="Download" onClick={() => handleDownloadFile(consent.code)}>
                                                                <DownloadRoundedIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Delete"
                                                                sx={{color: '#c62828'}}
                                                                onClick={() => handleFileDelete(consent.code)}
                                                            >
                                                                <DeleteOutlineRoundedIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )
                                                : (
                                                    <Button
                                                        size="small"
                                                        startIcon={<UploadFileRoundedIcon/>}
                                                        onClick={() => openUpload(consent)}
                                                        sx={{color: '#186BBE', textTransform: 'none', py: 0}}
                                                    >
                                                        Upload document
                                                    </Button>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
            <AdmicityDialog
                handleClose={closeUploadDialog}
                title={'Add Documents'}
                open={openUploadDialog}
                actions={[
                    {
                        label: 'Cancel',
                        onClick: closeUploadDialog
                    },
                    {
                        label: 'Upload',
                        onClick: handleFileUpload,
                        startIcon: isFileUploading ? <CircularProgress color="inherit" size={16}/> : undefined,
                        disabled: selectedFiles?.length === 0 || isUploadSizeExceedsLimit || isFileUploading,
                    }
                ]}
                maxWidth={'md'}
            >
                <Uploader
                    totalUploadSizeInMb={25}
                    onFilesSelect={setSelectedFiles}
                    onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                    singleFile
                    value={selectedFiles}
                />
            </AdmicityDialog>
        </Box>
    );
}

ConsentsSettings.propTypes = {
    consents: PropTypes.array,
    description: PropTypes.string,
    onDescriptionChange: PropTypes.func
};

export default Lookups;
